/**
 * TrainingGuides — /training. Cheat-sheet download cards up top (Quick Guide,
 * Crew Time & Attendance, Full Manual PDF) with the full manual rendered below.
 */
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, HelpCircle, FileText, Clock4, HardHat, Download, Loader2, ExternalLink } from "lucide-react";
import { mdComponents, slugify } from "@/pages/Guide";

const downloadPdf = async (path, filename) => {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  } catch (e) { toast.error(e?.message || "Could not download"); }
};

const GUIDES = [
  {
    key: "quick-guide", icon: HelpCircle, title: "Quick Guide",
    desc: "2-page cheat sheet — the whole deal lifecycle at a glance. Print and laminate for the truck.",
    action: () => downloadPdf("/docs/quick-guide.pdf", "SealTech CRM - Quick Reference.pdf"),
    label: "Download PDF",
  },
  {
    key: "crew-timeclock", icon: Clock4, title: "Crew Time & Attendance Instructions",
    desc: "One-pager for crew: install the Time Clock on their phone, clock in/out with GPS, expenses, mileage, and the Friday submit rule.",
    action: () => window.open("/timeclock-guide.pdf", "_blank"),
    label: "Open PDF", external: true,
  },
  {
    key: "sub-checkin", icon: HardHat, title: "Sub Check-In Instructions",
    desc: "One-pager for subcontractor foremen: install the Sub Check-In on their phone, check in on arrival and out at departure with GPS.",
    action: () => window.open("/subclock-guide.pdf", "_blank"),
    label: "Open PDF", external: true,
  },
  {
    key: "full-manual", icon: FileText, title: "Full Manual (PDF)",
    desc: "The complete user manual covering every feature — printable copy of the manual shown below.",
    action: () => downloadPdf("/docs/full-manual.pdf", "SealTech CRM - Full User Manual.pdf"),
    label: "Download PDF",
  },
];

export default function TrainingGuides() {
  const [md, setMd] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/guide").then((r) => setMd(r.data?.markdown || "")).catch(() => setError("Could not load the manual."));
  }, []);

  const toc = useMemo(() => {
    const rows = [];
    for (const line of md.split("\n")) {
      const m = line.match(/^(#{1,2})\s+(.*)/);
      if (m) rows.push({ depth: m[1].length, text: m[2].replace(/[*`]/g, ""), id: slugify(m[2].replace(/[*`]/g, "")) });
    }
    return rows.filter((r) => r.depth === 2 || rows.length < 40);
  }, [md]);

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="training-guides-page">
      <div className="mb-6 pb-6 border-b border-zinc-200">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700 mb-2 flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" /> Help & Training</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Training Guides</h1>
        <div className="text-sm text-zinc-600 mt-1">Cheat sheets to print or share, plus the full manual below.</div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="cheat-sheet-cards">
        {GUIDES.map((g) => (
          <div key={g.key} className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col hover:border-blue-700 transition-colors" data-testid={`guide-card-${g.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <g.icon className="w-4 h-4 text-blue-700" />
              <div className="font-heading font-bold text-sm">{g.title}</div>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed flex-1 mb-4">{g.desc}</p>
            <button onClick={g.action} data-testid={`guide-dl-${g.key}`}
              className="h-9 px-3 inline-flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white rounded-sm hover:bg-blue-700 transition-colors">
              {g.external ? <ExternalLink className="w-3 h-3" /> : <Download className="w-3 h-3" />} {g.label}
            </button>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-2xl font-black tracking-tight mb-4" data-testid="full-manual-heading">Full Manual</h2>
      {error && <div className="text-sm text-red-700">{error}</div>}
      {!md && !error && <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading manual…</div>}
      {md && (
        <div className="flex gap-8 items-start">
          <aside className="hidden lg:block w-64 shrink-0 sticky top-6 max-h-[85vh] overflow-y-auto border border-zinc-200 bg-white rounded-sm p-4" data-testid="manual-toc">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">In the manual</div>
            <nav className="space-y-1">
              {toc.map((h, i) => (
                <button key={i}
                  onClick={() => document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="block w-full text-left text-xs text-zinc-600 hover:text-blue-700 hover:underline leading-snug py-0.5">
                  {h.text}
                </button>
              ))}
            </nav>
          </aside>
          <article className="flex-1 min-w-0 bg-white border border-zinc-200 rounded-sm p-6 sm:p-10 max-w-4xl" data-testid="manual-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>
              {md}
            </ReactMarkdown>
          </article>
        </div>
      )}
    </div>
  );
}
